import React, { useState } from 'react';
import { Ticket, CheckCircle, XCircle, AlertCircle, Loader2, Gift, Clock, Tag } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AuthCore } from '../core/AuthCore';

interface VoucherValidation {
  valid: boolean;
  code: string;
  credit_amount: number;
  expires_at: string;
  batch_name: string;
  voucher_type?: string;
  membership_tier?: {
    package: string;
    credits: number;
    durationDays: number;
    displayName: string;
  };
  error?: string;
  actionUrl?: string;
  errorCode?: string;
}

const RedeemVoucherPage: React.FC = () => {
  const navigate = useNavigate();
  const [voucherCode, setVoucherCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [validationResult, setValidationResult] = useState<VoucherValidation | null>(null);

  const apiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'https://qvy6tktvec.execute-api.us-east-1.amazonaws.com/staging';

  const handleValidate = async () => {
    if (!voucherCode.trim()) {
      toast.error('Please enter a voucher code');
      return;
    }

    setIsValidating(true);
    try {
      const response = await fetch(`${apiUrl}/api/voucher/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: voucherCode.trim().toUpperCase() })
      });
      
      const data = await response.json();
      console.log('Validate response:', data);
      
      if (data.success && data.valid) {
        setValidationResult({
          valid: true,
          code: data.data.code,
          credit_amount: data.data.credit_amount,
          expires_at: data.data.expires_at,
          batch_name: data.data.batch_name,
          voucher_type: data.data.voucher_type,
          membership_tier: data.data.membership_tier
        });
        
        const benefitMessage = data.data.membership_tier 
          ? `Valid ${data.data.membership_tier.displayName} voucher!`
          : `Valid voucher for ${data.data.credit_amount} credits!`;
        toast.success(benefitMessage);
      } else {
        const errorMessage = data.message || data.error || 'Invalid voucher code';
        const actionUrl = data.actionUrl;
        const errorCode = data.error;
        
        setValidationResult({
          valid: false,
          code: voucherCode,
          credit_amount: 0,
          expires_at: '',
          batch_name: '',
          error: errorMessage,
          actionUrl,
          errorCode
        });
        
        // Don't show toast for MEMBERSHIP_REQUIRED or MOBILE_ONLY - will show in form
        if (errorCode !== 'MEMBERSHIP_REQUIRED' && errorCode !== 'MOBILE_ONLY') {
          toast.error(errorMessage);
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to validate voucher';
      toast.error(errorMessage);
      setValidationResult({
        valid: false,
        code: voucherCode,
        credit_amount: 0,
        expires_at: '',
        batch_name: '',
        error: errorMessage
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleRedeem = async () => {
    if (!validationResult?.valid) {
      toast.error('Please validate the voucher first');
      return;
    }

    setIsRedeeming(true);
    try {
      const token = await AuthCore.getInstance().getAccessToken();
      if (!token) {
        toast.error('Please login to redeem vouchers');
        navigate('/login');
        return;
      }

      const response = await fetch(`${apiUrl}/api/voucher/redeem`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: voucherCode })
      });
      
      const data = await response.json();
      console.log('Redeem response:', data);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (data.success && data.data) {
        const { credits_added, membership_package } = data.data;
        
        if (!credits_added) {
          console.error('No credits_added in response:', data);
          toast.error('Redemption failed - no credits added');
          return;
        }
        
        let successMessage = `Successfully redeemed ${credits_added.toLocaleString()} credits!`;
        if (membership_package) {
          successMessage = `${membership_package} membership activated with ${credits_added.toLocaleString()} credits!`;
        }
        
        toast.success(successMessage, { duration: 5000 });
        setVoucherCode('');
        setValidationResult(null);
      } else {
        const errorMessage = data.message || data.error || 'Failed to redeem voucher';
        const actionUrl = data.actionUrl;
        
        // For MEMBERSHIP_REQUIRED, update validation result to show in form
        if (data.error === 'MEMBERSHIP_REQUIRED' && actionUrl) {
          setValidationResult({
            valid: false,
            code: voucherCode,
            credit_amount: 0,
            expires_at: '',
            batch_name: '',
            error: errorMessage,
            actionUrl
          });
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Redeem error:', error);
      toast.error('Failed to redeem voucher');
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleCodeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setVoucherCode(value);
    setValidationResult(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-full mb-4">
              <Gift className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Redeem Voucher</h1>
            <p className="text-lg text-gray-600">Enter your voucher code to claim credits</p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 border border-purple-100">
            {/* Input Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voucher Code
              </label>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Ticket className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={voucherCode}
                    onChange={handleCodeInput}
                    placeholder="Enter voucher code"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase font-mono text-lg"
                    disabled={isValidating || isRedeeming}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !validationResult) {
                        handleValidate();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={handleValidate}
                  disabled={isValidating || isRedeeming || !voucherCode.trim()}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors flex items-center gap-2"
                >
                  {isValidating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5" />
                      Validate
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Enter the voucher code you received from your purchase
              </p>
            </div>

            {/* Validation Result */}
            {validationResult && (
              <div className={`p-6 rounded-xl mb-6 ${
                validationResult.valid 
                  ? 'bg-green-50 border-2 border-green-200' 
                  : validationResult.actionUrl
                    ? 'bg-orange-50 border-2 border-orange-300'
                    : 'bg-red-50 border-2 border-red-200'
              }`}>
                {validationResult.valid ? (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-green-100">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-2 text-green-900">
                        Valid Voucher!
                      </h3>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-green-800">
                          <Gift className="w-4 h-4" />
                          <span className="font-semibold">
                            {validationResult.membership_tier 
                              ? `${validationResult.membership_tier.displayName} - ${validationResult.membership_tier.credits.toLocaleString()} credits/month`
                              : `Credits: ${validationResult.credit_amount.toLocaleString()}`
                            }
                          </span>
                        </div>
                        {validationResult.batch_name && (
                          <div className="flex items-center gap-2 text-green-700">
                            <Tag className="w-4 h-4" />
                            <span className="text-sm">{validationResult.batch_name}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-green-700">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm">Expires: {formatDate(validationResult.expires_at)}</span>
                        </div>
                        {validationResult.membership_tier && (
                          <div className="mt-3 p-3 bg-green-100 rounded-lg">
                            <p className="text-sm text-green-900 font-medium">
                              This is a membership voucher that will activate your {validationResult.membership_tier.displayName} plan for {validationResult.membership_tier.durationDays} days.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : validationResult.errorCode === 'MOBILE_ONLY' ? (
                  // Mobile Only Voucher
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-100">
                        <AlertCircle className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-blue-900 mb-1">
                          Mobile App Only
                        </h3>
                        <p className="text-sm text-blue-800 mb-3">
                          This voucher can only be redeemed through the MetaPic Mobile App.
                        </p>
                        <a
                          href="https://play.google.com/store/apps/details?id=com.metapic.app"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-center"
                        >
                          Download on Google Play
                        </a>
                      </div>
                    </div>
                  </div>
                ) : validationResult.actionUrl ? (
                  // Membership Required (not invalid, just not eligible)
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-orange-100">
                        <AlertCircle className="w-6 h-6 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-orange-900 mb-1">
                          Membership Required
                        </h3>
                        <p className="text-sm text-orange-800">
                          This is a top-up voucher that requires an active membership to use.
                        </p>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => navigate(validationResult.actionUrl!)}
                      className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold flex items-center justify-center gap-2"
                    >
                      <Gift className="w-5 h-5" />
                      View Membership Plans →
                    </button>
                  </div>
                ) : (
                  // Actually invalid voucher
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-red-100">
                      <XCircle className="w-6 h-6 text-red-600" />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-2 text-red-900">
                        Invalid Voucher
                      </h3>
                      <p className="text-red-800 font-medium">
                        {validationResult.error || 'This voucher code is not valid'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Redeem Button */}
            {validationResult?.valid && (
              <button
                onClick={handleRedeem}
                disabled={isRedeeming}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed font-bold text-lg transition-all transform hover:scale-[1.02] flex items-center justify-center gap-3"
              >
                {isRedeeming ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Redeeming...
                  </>
                ) : (
                  <>
                    <Gift className="w-6 h-6" />
                    {validationResult.membership_tier 
                      ? `Activate ${validationResult.membership_tier.displayName} Membership`
                      : `Redeem Now - Get ${validationResult.credit_amount.toLocaleString()} Credits`
                    }
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RedeemVoucherPage;
